/**
 * get all permissions for a specific domain
 * e.g getAll(0, 4) will return 0b1111
 * getAll(0, 7) will return 0b1111111
 * getAll(4, 7) will return 0b1110000
 * @param start number
 * @param end number
 */
function getAll(start: bigint, end: bigint) {
  return ~(~0n << (end + 1n)) & (~0n << start);
}

type WithPermissions = { permissions: bigint | number | string };

export function hasUserPermission(user: WithPermissions, permission: UserPermissions) {
  return (BigInt(user.permissions || UserPermissions.none) & permission) !== 0n;
}

export function setUserPermission(user: WithPermissions, permission: UserPermissions) {
  return BigInt(user.permissions || UserPermissions.none) | permission;
}
export function removeUserPermission(user: WithPermissions, permission: UserPermissions) {
  return BigInt(user.permissions || UserPermissions.none) & ~permission;
}

export function hasExactUserPermission(user: WithPermissions, permission: UserPermissions) {
  return (BigInt(user.permissions || UserPermissions.none) & permission) === permission;
}

export function toggleUserPermission(user: WithPermissions, permission: UserPermissions) {
  // dont use xor to support siedcars.all, destinations.all etc
  if (hasExactUserPermission(user, permission)) {
    return removeUserPermission(user, permission);
  } else {
    return setUserPermission(user, permission);
  }
}

export function getAllPermissions() {
  return Object.values(UserPermissions)
    .reduce((acc, permission) => acc | permission, UserPermissions.none)
    .toString();
}

export function getViewOnlyPermissions() {
  const user = {
    permissions: UserPermissions.none.toString(),
  };

  for (const permissionKey of Object.keys(UserPermissions)) {
    if (permissionKey.includes("view") || permissionKey === "sidecars.notifications") {
      user.permissions = toggleUserPermission(user, UserPermissions[permissionKey as keyof typeof UserPermissions]).toString();
    }
  }

  return user.permissions;
}

export const GRANTABLE_PERMISSION_HIDDEN_KEYS: ReadonlySet<keyof typeof UserPermissions> = new Set(["saml.update", "saml.all"]);

// We use a bitmap so we can reduce the amount of space taken up on the user object,
// Permissions are seperated by domains. leave some spare space for future permissions
export type UserPermissions = bigint;
export const UserPermissions = {
  none: 0n,
  "sidecars.view": 1n << 0n,
  "sidecars.create": 1n << 1n,
  "sidecars.invite": 1n << 2n,
  "sidecars.update": 1n << 3n,
  "sidecars.delete": 1n << 4n,
  "sidecars.view_messages": 1n << 6n,
  "sidecars.notifications": 1n << 7n,
  "sidecars.all": getAll(0n, 7n),
  "destinations.view": 1n << 10n,
  "destinations.update": 1n << 11n,
  "destinations.delete": 1n << 12n,
  "destinations.all": getAll(10n, 12n),
  "saml.view": 1n << 18n,
  "saml.update": 1n << 19n,
  "saml.all": getAll(18n, 19n),
  "audit_log.view": 1n << 22n,
  "global_layout.view": 1n << 25n,
  "global_layout.update": 1n << 26n,
  "global_layout.all": getAll(25n, 26n),
  "notifications.view": 1n << 29n,
  "notifications.update": 1n << 30n,
  "notifications.all": getAll(29n, 30n),
  "user_management.view": 1n << 37n,
  "user_management.invite": 1n << 38n,
  "user_management.activate": 1n << 39n,
  "user_management.update_permissions": 1n << 40n,
  "user_management.view_groups": 1n << 41n,
  "user_management.update_groups": 1n << 42n,
  "user_management.assign_groups": 1n << 43n,
  "user_management.edit_user": 1n << 44n,
  "user_management.view_messages": 1n << 45n,
  "user_management.merge": 1n << 46n,
  "user_management.all": getAll(37n, 46n),
  "chat_groups.view": 1n << 48n,
  "im_groups.view": 1n << 49n,
  "analytics.view": 1n << 50n,
  "compliance.view": 1n << 51n,

  "dlp.view": 1n << 52n,
  "dlp.update": 1n << 53n,
  "dlp.view_analytics": 1n << 54n,
  "dlp.all": getAll(52n, 54n) | (1n << 65n) | (1n << 66n),
  "incident.view": 1n << 65n,
  "incident.update": 1n << 66n,
  "waba.view_settings": 1n << 56n,
  "waba.update_settings": 1n << 57n,
  "waba.all": getAll(56n, 57n),
  "twilio.view_settings": 1n << 60n,
  "twilio.update_settings": 1n << 61n,
  "twilio.all": getAll(60n, 61n),
  "messages_analytics.view": 1n << 70n,
  "settings.view_general": 1n << 75n,
  "settings.update_general": 1n << 76n,
  "settings.all": getAll(75n, 76n),
  "api_keys.view": 1n << 80n,
  "api_keys.update": 1n << 81n,
  "api_keys.all": getAll(80n, 81n),
  "url_policy.view": 1n << 84n,
  "url_policy.update": 1n << 85n,
  "url_policy.all": getAll(84n, 85n),
  "browser_url_policy.view": 1n << 158n,
  "browser_url_policy.update": 1n << 159n,
  "browser_url_policy.all": getAll(158n, 159n),
  "browser_controls.view": 1n << 160n,
  "browser_controls.update": 1n << 161n,
  "browser_controls.all": getAll(160n, 161n),
  "access_requests.view": 1n << 162n,
  "access_requests.update": 1n << 163n,
  "access_requests.all": getAll(162n, 163n),
  "keyword_insights.view": 1n << 88n,
  "keyword_insights.update": 1n << 89n,
  "keyword_insights.view_analytics": 1n << 90n,
  "keyword_insights.all": getAll(88n, 90n),
  "phones_whitelist.view": 1n << 92n,
  "phones_whitelist.update": 1n << 93n,
  "phones_whitelist.all": getAll(92n, 93n),
  "phones_blocklist.view": 1n << 100n,
  "phones_blocklist.update": 1n << 101n,
  "phones_blocklist.all": getAll(100n, 101n),
  "blocked_phones.view": 1n << 156n,
  "blocked_phones.update": 1n << 157n,
  "blocked_phones.all": getAll(156n, 157n),
  "tuvis_contacts.view": 1n << 96n,
  "tuvis_contacts.update": 1n << 97n,
  "tuvis_contacts.create": 1n << 98n,
  "tuvis_contacts.all": getAll(96n, 98n),
  "slack.view_settings": 1n << 102n,
  "slack.update_settings": 1n << 103n,
  "slack.all": getAll(102n, 103n),
  "fraud.view": 1n << 104n,
  "fraud.update": 1n << 105n,
  "fraud.view_incidents": 1n << 106n,
  "fraud.view_urls": 1n << 107n,
  "fraud.update_urls": 1n << 108n,
  "fraud.update_incidents": 1n << 109n,
  "fraud.all": getAll(104n, 109n),
  "teams.view_settings": 1n << 110n,
  "teams.update_settings": 1n << 111n,
  "teams.all": getAll(110n, 111n),
  "dashboards.view": 1n << 123n,
  "dashboards.update": 1n << 124n,
  "dashboards.create": 1n << 125n,
  "dashboards.all": getAll(123n, 125n),
  "custom_filters.view": 1n << 128n,
  "custom_filters.update": 1n << 129n,
  "custom_filters.all": getAll(128n, 129n),
  "archive.view_settings": 1n << 130n,
  "archive.update_settings": 1n << 131n,
  "archive.all": getAll(130n, 131n), // start next from 140n so we have space for more archive permissions

  "surveillance.view": 1n << 140n,
  "surveillance.update": 1n << 141n,
  "surveillance.view_incidents": 1n << 142n,
  "surveillance.view_analytics": 1n << 146n,
  "surveillance.update_incidents": 1n << 147n,
  "surveillance.all": getAll(140n, 142n) | (1n << 146n) | (1n << 147n),

  "bot_builder.view": 1n << 143n,
  "bot_builder.update": 1n << 144n,
  "bot_builder.view_analytics": 1n << 145n,
  "bot_builder.view_triggers": 1n << 148n,
  "bot_builder.trigger_flows": 1n << 149n,
  "bot_builder.all": getAll(143n, 145n) | (1n << 148n) | (1n << 149n),

  "elite_token.view": 1n << 150n,
  "elite_token.update": 1n << 151n,
  "elite_token.all": getAll(150n, 151n),

  "im_templates.view": 1n << 152n,
  "im_templates.create": 1n << 153n,
  "im_templates.update": 1n << 154n,
  "im_templates.delete": 1n << 155n,
  "im_templates.all": getAll(152n, 155n),
};
